/**
 * PTO / leave configuration — turns a PTO **type** into a real SAP posting
 * target: an absence type (AWART) + the leave WBS element for the employee's
 * cost center and that type.
 *
 * Model (v1.0.418): the admin DEFINES PTO types in Settings; a backend program
 * (ZCL_M12_PTO_PROVISION, triggered from Settings) then creates, for every cost
 * center that has active people, a project **`H-PTO<costCenter>`** with one
 * released WBS **per type** named **`H-PTO<costCenter><typeCode>`**. So the WBS a
 * leave line posts to is DERIVED, not stored per cost center:
 *
 *     WBS  = `H-PTO` + <10-digit cost center> + <type.code>      (e.g. H-PTO0014010002VAC)
 *     AWART= <type.awart>
 *
 * Backed by ONE config table `ts_pto_types` (served by the in-boundary ABAP
 * config store, or Supabase in the live build — both via `supabase.from(...)`):
 *   { id, code, label, awart, awart_text, requires_doc, active, sort }
 *
 * Deterministic fallback: if config has not loaded, a built-in type list (with
 * codes + AWART matching the seed) keeps "Fill as PTO" working.
 */
import type { TsEmployee } from '../types/timesheet.types'
import { sapChargeObjectId, normalizeWbsKey } from '../lib/sapChargeObjects'
import { supabase } from '@/lib/supabase'

export interface PtoTypeConfig {
  id: string
  /** Short WBS-safe suffix (uppercase alnum, <=5), e.g. VAC / SICK / JURY. */
  code: string
  label: string
  /** SAP absence type (AWART). */
  awart: string
  awartText?: string
  requiresDoc?: boolean
  active?: boolean
  sort?: number
}

export interface ResolvedPtoCharge {
  /** Synthetic charge-object id of the derived per-type PTO WBS. */
  chargeCodeId: string
  /** SAP absence type to stamp on the CATS record. */
  absenceType: string
  /** External PTO WBS id (H-PTO<cc><code>). */
  wbsExternal: string
  label: string
}

// Built-in defaults — mirror the seeded ts_pto_types (id, code, awart) so the
// resolver + picker work before (or without) a config load. Keep in sync.
const FALLBACK_TYPES: PtoTypeConfig[] = [
  { id: 'vacation', code: 'VAC', label: 'Vacation / Holiday', awart: '0100', awartText: 'Vacation', sort: 10, active: true },
  { id: 'sick', code: 'SICK', label: 'Sick Leave', awart: '0200', awartText: 'Illness', sort: 20, active: true },
  { id: 'bereavement', code: 'BRV', label: 'Bereavement', awart: '0530', awartText: 'Bereavement', sort: 30, active: true },
  { id: 'military', code: 'MIL', label: 'Military Leave', awart: '0650', awartText: 'Military Service', requiresDoc: true, sort: 40, active: true },
  { id: 'jury', code: 'JURY', label: 'Jury Duty', awart: '0520', awartText: 'Jury Duty', requiresDoc: true, sort: 50, active: true },
  { id: 'fmla', code: 'FMLA', label: 'FMLA', awart: '0510', awartText: 'FMLA', requiresDoc: true, sort: 60, active: true },
]

let _types: PtoTypeConfig[] = []
let _loaded = false

/** Normalize a cost center to the 10-digit zero-padded form used in WBS ids. */
function normCostCenter(cc: string | null | undefined): string {
  const t = (cc ?? '').trim()
  if (!t) return ''
  return /^\d+$/.test(t) ? t.padStart(10, '0') : t
}

function rowToType(r: Record<string, unknown>): PtoTypeConfig {
  return {
    id: String(r.id ?? r.category ?? ''),
    code: String(r.code ?? '').toUpperCase(),
    label: String(r.label ?? r.id ?? ''),
    awart: String(r.awart ?? ''),
    awartText: r.awart_text != null ? String(r.awart_text) : undefined,
    requiresDoc: r.requires_doc === true || r.requires_doc === 'true',
    active: r.active !== false && r.active !== 'false',
    sort: typeof r.sort === 'number' ? r.sort : Number(r.sort) || 0,
  }
}

/** Load PTO types into the module cache. Best-effort; failure keeps fallbacks. */
export async function loadPtoConfig(orgId = 'sap'): Promise<void> {
  try {
    const { data } = await supabase.from('ts_pto_types').select('*').eq('org_id', orgId)
    if (Array.isArray(data) && data.length > 0) {
      _types = (data as Record<string, unknown>[])
        .map(rowToType)
        .filter((t) => t.code && t.awart)
        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    }
    _loaded = true
  } catch (err) {
    console.warn('[timesheet-pto] config load failed; using fallback types', err)
  }
}

export function isPtoConfigLoaded(): boolean {
  return _loaded
}

/** The ACTIVE PTO types (config, else built-in fallback) — drives the picker. */
export function getPtoTypes(): PtoTypeConfig[] {
  const src = _types.length ? _types : FALLBACK_TYPES
  return src.filter((t) => t.active !== false)
}

function findType(typeId: string): PtoTypeConfig | undefined {
  const src = _types.length ? _types : FALLBACK_TYPES
  return src.find((t) => t.id === typeId) ?? src.find((t) => t.code === typeId.toUpperCase())
}

/**
 * Resolve an employee + PTO type id to a concrete posting target. The WBS is
 * derived (`H-PTO<costCenter><typeCode>`) to match what the provisioning program
 * creates. Returns null when the employee has no cost center or the type has no
 * code (nothing to derive a WBS from) — the caller should surface that.
 */
export function resolvePtoCharge(employee: TsEmployee | undefined | null, typeId: string): ResolvedPtoCharge | null {
  const cc = normCostCenter(employee?.sapCostCenter)
  const t = findType(typeId)
  if (!cc || !t || !t.code) return null
  const wbsExternal = `H-PTO${cc}${t.code}`
  return {
    chargeCodeId: sapChargeObjectId('wbs_element', normalizeWbsKey(wbsExternal)),
    absenceType: t.awart,
    wbsExternal,
    label: `${t.label} — ${wbsExternal}`,
  }
}

/**
 * Reverse lookup: given a charge-code id (or WBS id), if it is a provisioned PTO
 * leave WBS (`H-PTO<10-digit cc><typeCode>`), return the type's AWART so an entry
 * posted to it — however it was created (manual PTO line, template) — still lands
 * in CATS as a leave record. Null for ordinary work WBS.
 */
export function absenceTypeForChargeCode(chargeCodeId: string | null | undefined): string | null {
  const id = (chargeCodeId ?? '').trim()
  if (!id) return null
  const key = id.includes(':') ? id.slice(id.indexOf(':') + 1) : id
  const m = key.match(/^H-PTO\d{10}([A-Za-z0-9]+)$/)
  if (!m) return null
  const code = m[1]!.toUpperCase()
  const src = _types.length ? _types : FALLBACK_TYPES
  return src.find((t) => t.code.toUpperCase() === code)?.awart ?? null
}

/** True when the id/WBS is a provisioned PTO leave WBS. */
export function isPtoChargeCode(chargeCodeId: string | null | undefined): boolean {
  return absenceTypeForChargeCode(chargeCodeId) !== null
}

// ── Self-service provisioning (Settings) ─────────────────────────────────────
// The config-store ICF exposes a `pto_provision` op (submits the backend program
// ZCL_M12_PTO_PROVISION as a job) and stores its status under tbl=pto_prov.

const CFG_BASE = '/sap/bc/zm12cfg'

export interface PtoProvisionStatus {
  running?: boolean
  done?: boolean
  cost_centers?: number
  processed?: number
  remaining?: number
  projects_created?: number
  wbs_created?: number
  types?: number
  at?: string
  by?: string
}

/** Kick off the backend provisioning program (creates projects + WBS). */
export async function triggerPtoProvision(): Promise<{ ok: boolean; message?: string }> {
  try {
    const res = await fetch(CFG_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ op: 'pto_provision' }),
    })
    if (!res.ok) return { ok: false, message: `HTTP ${res.status}` }
    return { ok: true }
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : String(e) }
  }
}

/** Read the last provisioning run's status (direct ICF read — the status payload
 *  carries no org_id, so the PostgREST-shim's client-side org filter would drop it). */
export async function getPtoProvisionStatus(orgId = 'sap'): Promise<PtoProvisionStatus | null> {
  try {
    const res = await fetch(`${CFG_BASE}?tbl=pto_prov&org=${encodeURIComponent(orgId)}`, {
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
    if (!res.ok) return null
    const body = (await res.json()) as { rows?: PtoProvisionStatus[] }
    return body.rows?.[0] ?? null
  } catch {
    return null
  }
}
